import { ChangeEvent, PermDefinitionConstants, PermissionsDefinition } from "@mcleod/components";
import { ArrayUtil, StringUtil } from "@mcleod/core";
import { RowPermsAllowDeny } from "../models/ModelPermsAllowDeny";
import { PanelSecureItem, PanelSecureItemProps } from "./PanelSecureItem";
import { SecureItem } from "./SecureItem";
import { UserOrGroup } from "./UserOrGroup";
import { PermissionsKeyGenerator } from "./PermissionsKeyGenerator";


export class PanelMultiSecure extends PanelSecureItem {
    private readonly selectedItems: SecureItem[];
    private _commonAllows: Map<string, RowPermsAllowDeny[]>;
    private noAllowRowsFound: boolean;
    isAction: boolean;

    constructor(selectedItems: SecureItem[], props?: Partial<PanelSecureItemProps>) {
        super(props);
        this.selectedItems = selectedItems;
        this.labelSecurityType.text = this.getPermissionsDefinition().description;
        this.setPropsFromSelectedItems();
        this.permsGrant.labelHeading.text = this.isAction ? "Permissions for Multiple Actions" : "Permissions for Multiple Pages";
    }

    get someAllowed(): boolean {
        return this.commonAllows?.size > 0;
    }

    getPermissionsDefinition(): PermissionsDefinition {
        return this.isAction ? PermDefinitionConstants.ACTION : PermDefinitionConstants.ENTIRE_SCREEN;
    }

    override syncLabelDescription() {
        this.labelDescription.text = this.determineDescriptionText();
    }

    private determineDescriptionText(): string {

        if (!this.secured) {
            return this.isAction ? "Everyone can execute these actions" : "Everyone can see these pages";
        }

        if (this.someAllowed) {
            return this.isAction ? "Users or Groups with access to all actions" : "Users or Groups with access to all pages";
        }

        if (this.noAllowRowsFound) {
            return this.isAction ? "No one can execute these actions" : "No one can see these pages";
        }

        return this.isAction ? "No Users or Groups have access to all actions" : "No Users or Groups have access to all pages";
    }

    override alreadyAllowed(userOrGroup: UserOrGroup, id: string): boolean {
        const key = PermissionsKeyGenerator.createAllowDenyKey({ userOrGroup, id });
        return this.commonAllows?.has(key) ?? false;
    }

    override isUserAllowedByGroupMembership(userId: string): boolean {
        return this.commonGroups.some(group => this.permsUserCache.isUserInGroup(userId, group));
    }

    private get commonGroups(): string[] {
        return Array.from(this.commonAllows.keys())
            .filter(PermissionsKeyGenerator.isGroupKey)
            .map(PermissionsKeyGenerator.extractIdFromKey);
    }

    private get commonAllows() {
        return this._commonAllows;
    }

    private set commonAllows(value: Map<string, RowPermsAllowDeny[]>) {
        this._commonAllows = value;
        this.panelAllowed.removeAll();
        for (const key of value.keys()) {
            this.addAllowedItemPanel(key);
        }
    }

    private addCommonAllowDenyRows(rows: RowPermsAllowDeny[]) {
        const key = PermissionsKeyGenerator.createAllowDenyKey(ArrayUtil.getFirstElement(rows));
        if (StringUtil.isEmptyString(key))
            return;
        this.commonAllows.set(key, rows);
        this.addAllowedItemPanel(key);
    }

    // iterates over the selected menu items and collects all the common allow/deny rows
    private setPropsFromSelectedItems() {
        let secured: boolean;
        const allAllowRows = new Map<string, RowPermsAllowDeny[]>();
        this.isAction = undefined;

        for (const item of this.selectedItems) {
            const rowPermsSecureItem = this.permissionsSource.getSecureItemRow(item);
            if (secured === undefined) {
                secured = rowPermsSecureItem != null;
            } else if (secured !== (rowPermsSecureItem != null)) {
                throw new Error("Cannot modify permissions of secured and unsecured items at the same time");
            }

            if (this.isAction === undefined) {
                this.isAction = item.isAction;
            } else if (this.isAction !== item.isAction) {
                throw new Error("Cannot modify permissions of actions and menu items at the same time");
            }

            for (const row of this.permissionsSource.getAllowDenyRows(item)) {
                const key = PermissionsKeyGenerator.createAllowDenyKey(row);
                if (!allAllowRows.has(key)) {
                    allAllowRows.set(key, []);
                }
                allAllowRows.get(key).push(row);
            }
        }
        this.secured = secured ?? false;

        this.noAllowRowsFound = (allAllowRows.size === 0);

        // Filter the map to only include entries with rows that are common across all selected items
        this.commonAllows = new Map([...allAllowRows.entries()].filter(([_, rows]) => rows.length === this.selectedItems.length));
    }

    async switchSecuredOnChange(event: ChangeEvent) {
        if (!event.userInitiatedChange)
            return;
        const secured = event.newValue === true;
        if (secured) {
            this.selectedItems.forEach(secureItem => this.permissionsSource.addSecureItemRow(secureItem));
        } else {
            this.selectedItems.forEach(secureItem => this.permissionsSource.deleteSecureItem(secureItem));
        }
        this.secured = secured;
    }

    override internalAddAllowed(userOrGroup: UserOrGroup, id: string) {
        const rows: RowPermsAllowDeny[] = [];
        for (const item of this.selectedItems) {
            const allowDeny = this.addAllowedToPermissionsSource(item, userOrGroup, id);
            rows.push(allowDeny);
            item.allowDenyChanged?.();
        }
        this.addCommonAllowDenyRows(rows);
    }

    addAllowedItemPanel(key: string) {
        const rows = this.commonAllows.get(key);
        const firstRow = ArrayUtil.getFirstElement(rows);
        if (firstRow == null)
            return;
        const result = this.createAllowItemPanel(
            firstRow,
            () => {
                const rowsToDelete = this.commonAllows.get(key);
                rowsToDelete.forEach(row => this.permissionsSource.deleteAllowDenyRow(row));
                this.commonAllows.delete(key);
                this.panelAllowed.remove(result);
                this.selectedItems.forEach(item => item.allowDenyChanged?.());
            }
        );
        this.panelAllowed.add(result);
    }
}
